import { isEmail, convertMomentFormat } from './_utils';
import { getNavigationBarHeight } from './navigation';
import debounce from 'lodash/debounce';
import assign from 'lodash/assign';
import { parse } from 'date-fns';

const REQUIRED = 'required';
const INVALID = 'aria-invalid';
const validFieldClass = 'formElement-field--valid';
const invalidFieldClass = 'formElement-field--invalid';
const filledFieldClass = 'formElement-field--filled';
const hiddenErrorClass = 'ContactForm-error--hidden';
const formSelector = '.ContactForm-form';
const messageSelector = '.formElement-message';
const fieldSelector = '.formElement-field';
const validatedFormSelector = '.ValidatedForm';
const labelSelector = '.formElement-label';

function isValidField($field) {
	const required = $field.prop('required');
	const value = $field.val();
	let valid;
	let hasValue;

	if ($field.is('select') && $field.prop('multiple')) {
		hasValue = !!(value && value.filter(value => value && value.trim().length > 0).length > 0);
	} else {
		hasValue = !!(value && value.trim().length > 0);
	}

	//helper function to return true if things are valid thus far,
	//the value is empty but the field is optional,
	//or if whatever other criteria is met
	let emptyAndOptionalOr = conditionalFunc =>
		valid && ((!required && !hasValue) || conditionalFunc());

	// required check
	valid = required ? hasValue : true;

	if ($field.attr('type') === 'email') {
		valid = emptyAndOptionalOr(() => isEmail(value));
	}

	if ($field.parent().hasClass('TypeAhead-textField')) {
		$field.trigger('validate');
		valid = $field.data('valid');
	}

	if ($field.attr('pattern')) {
		valid = emptyAndOptionalOr(() => {
			const pattern = $field.attr('pattern');
			const fieldRegExp = new RegExp(pattern);
			return fieldRegExp.test(value);
		});
	}

	if ($field.data('dateformat')) {
		valid = emptyAndOptionalOr(() => {
			const dateFormat = $field.data('dateformat');
			const allowPastDate = $field.data('allow-past-date');

			// Parsing date string based on the provided format
			const dateValue = parse(value, convertMomentFormat(dateFormat), new Date());

			// Actual date
			const dateNow = new Date();

			// Checking for year, month, and day equality
			const isSameOrAfter = dateValue.getTime() >= dateNow.setHours(0, 0, 0, 0);

			return !isNaN(dateValue.getTime()) && (isSameOrAfter || allowPastDate);
		});
	}

	if ($field.attr('type') === 'checkbox') {
		valid = required ? $field.prop('checked') : true;
	}

	$field.get(0).setCustomValidity(valid ? '' : $field.data('errorMessage'));
	return valid;
}

export function validateField($field) {
	$field.siblings(messageSelector).remove();
	$field.off('input.form');
	const value = $field.val();
	const hasValue =
		$field.attr('type') === 'checkbox' ? $field.prop('checked') : value && value.length > 0;
	if (hasValue) {
		$field.addClass(filledFieldClass);
	} else {
		$field.removeClass(filledFieldClass);
	}

	if (isValidField($field)) {
		$field.attr(INVALID, false);
		if (hasValue) {
			$field.addClass(validFieldClass);
			$field.removeClass(invalidFieldClass);
		} else {
			$field.removeClass(validFieldClass);
		}
	} else {
		const message = $field.data('errorMessage');
		const $alert = $(`<span class="formElement-message" role="alert">${message}</span>`);
		$field.parent().append($alert);
		$field.attr(INVALID, true);
		$field.addClass(invalidFieldClass);
		$field.removeClass(validFieldClass);
		$field.on('input.form', e => validateField($(e.target)));
	}
}

function validateForm($container, $fields) {
	const $form = $container.find(formSelector);
	const formDOM = $container.find(formSelector).get(0);
	const $formError = $(`#${$container.data('errorId')}`);
	const $errors = $fields.filter((i, field) => $(field).hasClass(invalidFieldClass));
	// get the height of the label for better scroll effect on error messages
	// +2 to add some extra top spacing between navigation and label
	const labelOffset = $fields.first().parent().find(labelSelector).height() + 2;

	if (formDOM && !formDOM.checkValidity() && $errors.length > 0) {
		const buttons = $errors
			.map((i, field) => {
				const $field = $(field);
				return `
        <button class="CTA CTA--underlined--red" onClick="$('html, body').animate(
			{
				scrollTop: $('#${$field.attr(
					'id'
				)}').parent().find('${labelSelector}').offset().top - ${getNavigationBarHeight()} - ${labelOffset}
			}, 500);
			$('#${$field.attr('id')}').focus()">${$field.data('validationMessage')}</button>`;
			})
			.get();
		const message = $(`
    <div>
      <span>${$form.data('validationMessage')}</span>
    </div>`).append(`
      <div>
        ${buttons.join(',')}
      </div>`);
		setTimeout(
			() => {
				$formError.empty().append(message);
				toggleError($formError, 'on', 500);
			},
			$formError.find('.CTA').length > 0 ? 300 : 0
		);
		return;
	}
	toggleError($formError, 'off', 500);
}

const debouncedValidateForm = debounce(validateForm, 200);

/**
 * toggleError: Debounced function to show or hide the error message div
 *
 * @param {jQuery}	$formError - jQuery object for the form error div
 * @param {String}	action - Action to perform: 'on' to show. Hide otherwise
 * @param {Number}	duration - Duration of the animation
 * @param {Object}	_options - Options object to pass to the jQuery slide functions
 *
 */
export const toggleError = debounce(($formError, action = 'on', duration = 0, _options = {}) => {
	const options = { duration };
	if (typeof _options == 'object') {
		assign(options, _options);
	}
	if (action === 'on') {
		$formError.slideDown(options);
	} else {
		$formError.slideUp(options);
	}
}, 300);

export function updateValidatedFields($container) {
	const $fields = $container.find(fieldSelector);
	$(formSelector).off('submit.form'); //remove the old one first
	$(formSelector).on('submit.form', e => {
		const navigationBarHeight = getNavigationBarHeight();

		e.preventDefault();
		$fields.each((i, field) => validateField($(field)));
		debouncedValidateForm.cancel();
		validateForm($container, $fields);
		if ($container.find(formSelector).is(':invalid')) {
			$('html, body').animate(
				{ scrollTop: $formError.offset().top - navigationBarHeight },
				500
			);
		}
	});
	$fields.off('blur');
	$fields.on('blur', e => {
		const $field = $(e.target);
		validateField($field);
		debouncedValidateForm($container, $fields);
	});
	//check validity right away so we can remove the error banner if its no longer applicable
	debouncedValidateForm($container, $fields);
}

function setupValidation($container) {
	const $formError = $(`#${$container.data('errorId')}`);
	toggleError($formError, 'off', 0);
	const $fields = $container.find(fieldSelector);

	$fields.each((i, field) => {
		const $field = $(field);

		// setCustomValidity seems to force checkValidity() to return
		// false for select elements unless that element receives a blut
		// This causes a false failure if there is a value set on the select
		// but the form is submitted without being touched. The isValidField
		// function will only setCustomValidity if the field isn't already valid
		if ($field.is('select')) {
			isValidField($field);
		} else if ($field.prop('required')) {
			field.setCustomValidity($field.data('errorMessage'));
		} else {
			field.setCustomValidity('');
		}
	});

	$fields.on('blur', e => {
		const $field = $(e.target);
		validateField($field);
		debouncedValidateForm($container, $fields);
	});

	$(formSelector).on('validate.form', e => {
		debouncedValidateForm($container, $fields);
	});

	$(formSelector).on('submit.form', e => {
		const navigationBarHeight = getNavigationBarHeight();

		e.preventDefault();
		$fields.each((i, field) => validateField($(field)));
		debouncedValidateForm.cancel();
		validateForm($container, $fields);
		if ($container.find(formSelector).is(':invalid')) {
			$('html, body').animate(
				{ scrollTop: $formError.offset().top - navigationBarHeight },
				500
			);
		}
	});
}

export default function init() {
	$(validatedFormSelector).each((i, container) => setupValidation($(container)));
}
